import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import AuthGate from './shell/AuthGate';
import Layout from './shell/Layout';
import { ToastContainer } from './components/ui/Toast';

const LoginPage = lazy(() => import('./modules/auth/LoginPage'));
const DashboardPage = lazy(() => import('./modules/dashboard/DashboardPage'));
const CommandCenter = lazy(() => import('./modules/dashboard/CommandCenter'));
const ProductsListPage = lazy(() => import('./modules/products/ProductsListPage'));
const ProductDetailPage = lazy(() => import('./modules/products/ProductDetailPage'));
const ProductEditPage = lazy(() => import('./modules/products/ProductEditPage'));
const ProductOnboardingPage = lazy(() => import('./modules/products/ProductOnboardingPage'));
const OrderingPage = lazy(() => import('./modules/ordering/OrderingPage'));
const PhotoCardsPage = lazy(() => import('./modules/ordering/PhotoCardsPage'));
const ReceivingPage = lazy(() => import('./modules/receiving/ReceivingPage'));
const TillCheckPage = lazy(() => import('./modules/till-check/TillCheckPage'));
const OosPage = lazy(() => import('./modules/oos/OosPage'));
const PayrollPage = lazy(() => import('./modules/payroll/PayrollPage'));
const WebsitePage = lazy(() => import('./modules/website/WebsitePage'));
const OrdersPage = lazy(() => import('./modules/website/OrdersPage'));
const CustomersPage = lazy(() => import('./modules/website/CustomersPage'));
const LoyaltyPage = lazy(() => import('./modules/website/LoyaltyPage'));
const WebsiteProductsPage = lazy(() => import('./modules/website/WebsiteProductsPage'));
const ContentPage = lazy(() => import('./modules/website/ContentPage'));
const SEOPage = lazy(() => import('./modules/website/SEOPage'));
const AnalyticsPage = lazy(() => import('./modules/website/AnalyticsPage'));
const ClassificationTemplatesPage = lazy(() => import('./modules/website/ClassificationTemplatesPage'));
const CollectionsPage = lazy(() => import('./modules/website/CollectionsPage'));
const DiscountsPage = lazy(() => import('./modules/website/DiscountsPage'));
const WebsiteDashboardPage = lazy(() => import('./modules/website/WebsiteDashboardPage'));
const CategoryMappingPage = lazy(() => import('./modules/website/CategoryMappingPage'));
const ThemeCustomizerPage = lazy(() => import('./modules/website/ThemeCustomizerPage'));
const StorefrontSettingsPage = lazy(() => import('./modules/website/StorefrontSettingsPage'));
const CodeEditorPage = lazy(() => import('./modules/website/CodeEditorPage'));
const InventoryHoldsPage = lazy(() => import('./modules/website/InventoryHoldsPage'));
const PosPage = lazy(() => import('./modules/pos/PosPage'));
const OpsPage = lazy(() => import('./modules/ops/OpsPage'));
const CompetitorsPage = lazy(() => import('./modules/competitors/CompetitorsPage'));
const CockpitPage = lazy(() => import('./modules/cockpit/CockpitPage'));

const StockoutsPage = lazy(() => import('./modules/stockouts/StockoutsPage'));

// Purchase Orders
const PurchaseOrdersPage = lazy(() => import('./modules/purchase-orders/PurchaseOrdersPage'));

// Supply Chain
const MBLLCatalogPage = lazy(() => import('./modules/supply-chain/MBLLCatalogPage'));
const InventoryDepthPage = lazy(() => import('./modules/supply-chain/InventoryDepthPage'));

// Integrations
const CovaReportsPage = lazy(() => import('./modules/integrations/CovaReportsPage'));
const WhenIWorkPage = lazy(() => import('./modules/integrations/WhenIWorkPage'));
const NuclinoPage = lazy(() => import('./modules/integrations/NuclinoPage'));
const SlackPage = lazy(() => import('./modules/integrations/SlackPage'));
const MBLLPage = lazy(() => import('./modules/integrations/MBLLPage'));
const GoogleWorkspacePage = lazy(() => import('./modules/integrations/GoogleWorkspacePage'));
const WebhooksPage = lazy(() => import('./modules/integrations/WebhooksPage'));
const WooCommercePage = lazy(() => import('./modules/integrations/WooCommercePage'));

// Projects
const ProjectsPage = lazy(() => import('./modules/projects/ProjectsPage'));

// Platform
const DeploymentsPage = lazy(() => import('./modules/platform/DeploymentsPage'));
const BackupsPage = lazy(() => import('./modules/platform/BackupsPage'));
const CodebasePage = lazy(() => import('./modules/platform/CodebasePage'));
const AuditLogPage = lazy(() => import('./modules/platform/AuditLogPage'));
const ActivityPage = lazy(() => import('./modules/platform/ActivityPage'));
const AppsPage = lazy(() => import('./modules/platform/AppsPage'));
const HealthMonitorPage = lazy(() => import('./modules/platform/HealthMonitorPage'));

// Admin
const ThemesPage = lazy(() => import('./modules/admin/ThemesPage'));
const ProfitabilityPage = lazy(() => import('./modules/profitability/ProfitabilityPage'));
const PaymentsPage = lazy(() => import('./modules/admin/PaymentsPage'));
const ChangeFloatAdminPage = lazy(() => import('./modules/admin/ChangeFloatAdminPage'));
const DailyDutiesAdminPage = lazy(() => import('./modules/admin/DailyDutiesAdminPage'));
const InventoryErrorsPage = lazy(() => import('./modules/admin/InventoryErrorsPage'));
const TvPage = lazy(() => import('./modules/tv/TvPage'));
const DoorClosuresAdminPage = lazy(() => import('./modules/admin/DoorClosuresAdminPage'));
const HelpDeskPage = lazy(() => import('./modules/admin/HelpDeskPage'));
const StaffManagementPage = lazy(() => import('./modules/admin/StaffManagementPage'));
const UserAdoptionPage = lazy(() => import('./modules/admin/UserAdoptionPage'));
const BudtenderPage = lazy(() => import('./modules/admin/BudtenderPage'));
const SagePage = lazy(() => import('./modules/admin/SagePage'));
const RuleViolationsPage = lazy(() => import('./modules/admin/RuleViolationsPage'));
const AgentDashboardPage = lazy(() => import('./modules/admin/AgentDashboardPage'));
const AgentDetailPage = lazy(() => import('./modules/admin/AgentDetailPage'));
const CustomerOrdersAdminPage = lazy(() => import('./modules/admin/CustomerOrdersAdminPage'));
const AgentActionsPage = lazy(() => import('./modules/admin/AgentActionsPage'));
const EmployeeManualAdminPage = lazy(() => import('./modules/admin/EmployeeManualAdminPage'));
const EmployeeInfoPage = lazy(() => import('./modules/admin/EmployeeInfoPage'));
const SignalAdminPage = lazy(() => import('./modules/admin/SignalAdminPage'));
const SignalPerformancePage = lazy(() => import('./modules/admin/SignalPerformancePage'));
const VendorCampaignsPage = lazy(() => import('./modules/admin/VendorCampaignsPage'));

// Reports
const ReportsHub = lazy(() => import('./modules/reports/ReportsHub'));
const SalesReport = lazy(() => import('./modules/reports/SalesReport'));
const FinancialReport = lazy(() => import('./modules/reports/FinancialReport'));
const InventoryReport = lazy(() => import('./modules/reports/InventoryReport'));
const CustomerReport = lazy(() => import('./modules/reports/CustomerReport'));
const BudtenderScorecard = lazy(() => import('./modules/reports/BudtenderScorecard'));

// Intelligence
const IntelligencePage = lazy(() => import('./modules/intelligence/IntelligencePage'));
const KbAnalyticsPage = lazy(() => import('./modules/intelligence/KbAnalyticsPage'));

// AI
const AiPage = lazy(() => import('./modules/ai/AiPage'));

// Sales & Clock
const SalesClockPage = lazy(() => import('./modules/sales-clock/SalesClockPage'));

// Klaviyo
const KlaviyoCampaignPage = lazy(() => import('./modules/klaviyo/KlaviyoCampaignPage'));

// Attendance
const AttendancePage = lazy(() => import('./modules/attendance/AttendancePage'));
const EmployeesPage = lazy(() => import('./modules/employees/EmployeesPage'));

// HR
const HiringPage = lazy(() => import('./modules/hr/HiringPage'));
const InterviewPage = lazy(() => import('./modules/hr/InterviewPage'));
const TrainingDashboardPage = lazy(() => import('./modules/hr/TrainingDashboardPage'));
const TrainingEditorPage = lazy(() => import('./modules/hr/TrainingEditorPage'));
const TrainingQuizEditorPage = lazy(() => import('./modules/hr/TrainingQuizEditorPage'));
const AdvancedModuleEditorPage = lazy(() => import('./modules/hr/AdvancedModuleEditorPage'));
const DutiesEditorPage = lazy(() => import('./modules/hr/DutiesEditorPage'));

// Dev Dashboard
const DevLayout = lazy(() => import('./modules/dev/DevLayout'));
const DevDashboard = lazy(() => import('./modules/dev/DevDashboard'));

// Staff Order Builder
const StaffLayout = lazy(() => import('./modules/staff/StaffLayout'));
const CustomerLookupPage = lazy(() => import('./modules/staff/CustomerLookupPage'));
const OrderBuilderPage = lazy(() => import('./modules/staff/OrderBuilderPage'));
const OrderConfirmPage = lazy(() => import('./modules/staff/OrderConfirmPage'));

// Attestation (public form, no auth)
const AttestationFormPage = lazy(() => import('./modules/attestation/AttestationFormPage'));

// Store Hub (staff portal)
const StoreLayout = lazy(() => import('./modules/store/StoreLayout'));
const StoreLoginPage = lazy(() => import('./modules/store/StoreLoginPage'));
const StoreHub = lazy(() => import('./modules/store/StoreHub'));
const StoreAppPage = lazy(() => import('./modules/store/StoreAppPage'));
const CustomerRequestPage = lazy(() => import('./modules/store/CustomerRequestPage'));
const DutiesPage = lazy(() => import('./modules/store/DutiesPage'));
const MyPerformancePage = lazy(() => import('./modules/store/MyPerformancePage'));
const TrainingPage = lazy(() => import('./modules/store/TrainingPage'));
const ChangeFloatPage = lazy(() => import('./modules/store/ChangeFloatPage'));
const DailyDutiesPage = lazy(() => import('./modules/store/DailyDutiesPage'));
const DoorClosuresPage = lazy(() => import('./modules/store/DoorClosuresPage'));
const BugReportPage = lazy(() => import('./modules/store/BugReportPage'));
const RuleViolationPage = lazy(() => import('./modules/store/RuleViolationPage'));
const LotteryPage = lazy(() => import('./modules/store/LotteryPage'));
const LotterySOP = lazy(() => import('./modules/store/LotterySOP'));
const LotteryAdminPage = lazy(() => import('./modules/admin/LotteryAdminPage'));
const EmployeeManualPage = lazy(() => import('./modules/store/EmployeeManualPage'));
const SignalPage = lazy(() => import('./modules/store/SignalPage'));
const LaunchSequenceHome = lazy(() => import('./modules/launch/LaunchSequenceHome'));
const BriefingFlow = lazy(() => import('./modules/launch/BriefingFlow'));
const StandupFlow = lazy(() => import('./modules/launch/StandupFlow'));
const CigarClubPage = lazy(() => import('./modules/cigar-club/CigarClubPage'));
const HiveNcPage = lazy(() => import('./modules/hive-nc/HiveNcPage'));
const HiveAccessoriesPage = lazy(() => import('./modules/hive-accessories/HiveAccessoriesPage'));
const JgCatalogPage = lazy(() => import('./modules/jg-catalog/JgCatalogPage'));
const FlightDeckPage = lazy(() => import('./modules/flight-deck/FlightDeckPage'));
const FlightDeckSettingsPage = lazy(() => import('./modules/flight-deck/FlightDeckSettingsPage'));

const Loading = () => {
  const isStore = window.location.pathname.startsWith('/store');
  return (
    <div className={`flex items-center justify-center h-screen ${isStore ? 'bg-jp-bg' : ''}`}>
      <div className={`animate-spin rounded-full h-8 w-8 border-2 border-t-transparent ${isStore ? 'border-jp-purple' : 'border-gray-400'}`} />
    </div>
  );
};

export default function App() {
  return (
    <AuthGate>
      <ToastContainer />
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route element={<Layout />}>
            <Route path="/dashboard" element={<CommandCenter />} />
            <Route path="/dashboard/legacy" element={<DashboardPage />} />
            <Route path="/products" element={<ProductsListPage />} />
            <Route path="/products/:sku" element={<ProductDetailPage />} />
            <Route path="/products/:sku/edit" element={<ProductEditPage />} />
            <Route path="/products/onboarding" element={<ProductOnboardingPage />} />
            <Route path="/ordering" element={<OrderingPage />} />
            <Route path="/photo-cards" element={<PhotoCardsPage />} />
            <Route path="/receiving" element={<ReceivingPage />} />
            <Route path="/till-check" element={<TillCheckPage />} />
            <Route path="/stockouts" element={<StockoutsPage />} />
            <Route path="/purchase-orders" element={<PurchaseOrdersPage />} />
            <Route path="/out-of-stock" element={<OosPage />} />
            <Route path="/mbll-catalog" element={<MBLLCatalogPage />} />
            <Route path="/inventory-depth" element={<InventoryDepthPage />} />
            <Route path="/payroll" element={<PayrollPage />} />
            <Route path="/website/orders" element={<OrdersPage />} />
            <Route path="/website/holds" element={<InventoryHoldsPage />} />
            <Route path="/website/products" element={<WebsiteProductsPage />} />
            <Route path="/website/customers" element={<CustomersPage />} />
            <Route path="/website/loyalty" element={<LoyaltyPage />} />
            <Route path="/website/content" element={<ContentPage />} />
            <Route path="/website/seo" element={<SEOPage />} />
            <Route path="/website/analytics" element={<AnalyticsPage />} />
            <Route path="/website/templates" element={<ClassificationTemplatesPage />} />
            <Route path="/website/collections" element={<CollectionsPage />} />
            <Route path="/website/discounts" element={<DiscountsPage />} />
            <Route path="/website/categories" element={<CategoryMappingPage />} />
            <Route path="/website/theme" element={<ThemeCustomizerPage />} />
            <Route path="/website/settings" element={<StorefrontSettingsPage />} />
            <Route path="/website/code" element={<CodeEditorPage />} />
            <Route path="/website" element={<WebsiteDashboardPage />} />
            <Route path="/website-preview" element={<WebsitePage />} />
            <Route path="/cigar-club" element={<CigarClubPage />} />
            <Route path="/hive-nc" element={<HiveNcPage />} />
            <Route path="/hive-accessories" element={<HiveAccessoriesPage />} />
            <Route path="/jg-catalog" element={<JgCatalogPage />} />
            <Route path="/pos" element={<PosPage />} />
            <Route path="/ops" element={<OpsPage />} />
            <Route path="/tv" element={<TvPage />} />
            <Route path="/competitors" element={<CompetitorsPage />} />
            <Route path="/cockpit" element={<CockpitPage />} />
            {/* Integrations */}
            <Route path="/integrations/cova" element={<CovaReportsPage />} />
            <Route path="/integrations/wiw" element={<WhenIWorkPage />} />
            <Route path="/integrations/nuclino" element={<NuclinoPage />} />
            <Route path="/integrations/slack" element={<SlackPage />} />
            <Route path="/integrations/mbll" element={<MBLLPage />} />
            <Route path="/integrations/google" element={<GoogleWorkspacePage />} />
            <Route path="/integrations/webhooks" element={<WebhooksPage />} />
            <Route path="/integrations/woocommerce" element={<WooCommercePage />} />
            <Route path="/integrations/sage" element={<SagePage />} />
            {/* Projects */}
            <Route path="/projects" element={<ProjectsPage />} />
            {/* Platform */}
            <Route path="/platform/deployments" element={<DeploymentsPage />} />
            <Route path="/platform/backups" element={<BackupsPage />} />
            <Route path="/platform/code" element={<CodebasePage />} />
            <Route path="/platform/audit" element={<AuditLogPage />} />
            <Route path="/platform/activity" element={<ActivityPage />} />
            <Route path="/platform/apps" element={<AppsPage />} />
            <Route path="/platform/health" element={<HealthMonitorPage />} />
            {/* Admin */}
            <Route path="/admin/themes" element={<ThemesPage />} />
            <Route path="/admin/payments" element={<PaymentsPage />} />
            <Route path="/admin/profitability" element={<ProfitabilityPage />} />
            <Route path="/admin/change-float" element={<ChangeFloatAdminPage />} />
            <Route path="/admin/inventory-errors" element={<InventoryErrorsPage />} />
            <Route path="/admin/door-closures" element={<DoorClosuresAdminPage />} />
            <Route path="/admin/rule-violations" element={<RuleViolationsPage />} />
            <Route path="/admin/help-desk" element={<HelpDeskPage />} />
            <Route path="/admin/staff" element={<StaffManagementPage />} />
            <Route path="/admin/budtender" element={<BudtenderPage />} />
            <Route path="/admin/lottery" element={<LotteryAdminPage />} />
            <Route path="/admin/user-adoption" element={<UserAdoptionPage />} />
            <Route path="/admin/agents" element={<AgentDashboardPage />} />
            <Route path="/admin/agents/:slug" element={<AgentDetailPage />} />
            <Route path="/admin/customer-requests" element={<CustomerOrdersAdminPage />} />
            <Route path="/admin/agent-actions" element={<AgentActionsPage />} />
            <Route path="/admin/employee-manual" element={<EmployeeManualAdminPage />} />
            <Route path="/admin/employee-info" element={<EmployeeInfoPage />} />
            <Route path="/admin/signal" element={<SignalAdminPage />} />
            <Route path="/admin/signal-performance" element={<SignalPerformancePage />} />
            <Route path="/admin/vendor-campaigns" element={<VendorCampaignsPage />} />
            {/* Reports */}
            <Route path="/reports" element={<ReportsHub />} />
            <Route path="/reports/sales" element={<SalesReport />} />
            <Route path="/reports/financial" element={<FinancialReport />} />
            <Route path="/reports/inventory" element={<InventoryReport />} />
            <Route path="/reports/customers" element={<CustomerReport />} />
            <Route path="/reports/budtender" element={<BudtenderScorecard />} />
            {/* Intelligence */}
            <Route path="/intelligence" element={<IntelligencePage />} />
            <Route path="/kb-analytics" element={<KbAnalyticsPage />} />
            {/* AI */}
            <Route path="/ai" element={<AiPage />} />
            {/* Sales & Clock */}
            <Route path="/sales-clock" element={<SalesClockPage />} />
            {/* Klaviyo */}
            <Route path="/klaviyo" element={<KlaviyoCampaignPage />} />
            {/* Attendance */}
            <Route path="/attendance" element={<AttendancePage />} />
            <Route path="/employees" element={<EmployeesPage />} />
            {/* HR */}
            <Route path="/hr/hiring" element={<HiringPage />} />
            <Route path="/hr/interview/:id" element={<InterviewPage />} />
            <Route path="/hr/training" element={<TrainingDashboardPage />} />
            <Route path="/hr/training-editor" element={<TrainingEditorPage />} />
            <Route path="/hr/training-quiz-editor" element={<TrainingQuizEditorPage />} />
            <Route path="/hr/advanced-module-editor" element={<AdvancedModuleEditorPage />} />
            <Route path="/hr/duties-editor" element={<DutiesEditorPage />} />
            <Route path="/daily-duties" element={<DailyDutiesAdminPage />} />
          </Route>
          {/* Dev Dashboard - separate layout, no sidebar */}
          <Route path="/dev" element={<DevLayout />}>
            <Route index element={<DevDashboard />} />
          </Route>
          {/* Staff Order Builder - mobile-optimized, no sidebar */}
          <Route path="/staff" element={<StaffLayout />}>
            <Route index element={<CustomerLookupPage />} />
            <Route path="order" element={<OrderBuilderPage />} />
            <Route path="confirm/:orderId" element={<OrderConfirmPage />} />
          </Route>
          {/* Store Hub - staff portal, Jupiter branded */}
          <Route path="/store/login" element={<StoreLoginPage />} />
          <Route path="/store" element={<StoreLayout />}>
            <Route index element={<StoreHub />} />
            <Route path="oos" element={<StoreAppPage app="oos" title="Out of Stock" />} />
            <Route path="receiving" element={<StoreAppPage app="receiving" title="Receiving" />} />
            <Route path="till-check" element={<StoreAppPage app="tillCheck" title="Till Check" />} />
            <Route path="stockouts" element={<StockoutsPage />} />
            <Route path="customer-requests" element={<CustomerRequestPage />} />
            <Route path="duties" element={<DutiesPage />} />
            <Route path="training" element={<TrainingPage />} />
            <Route path="my-performance" element={<MyPerformancePage />} />
            <Route path="web-orders" element={<StoreAppPage app="webOrders" title="Web Orders" />} />
            <Route path="staff-purchases" element={<StoreAppPage app="staffPurchases" title="Staff Purchases" />} />
            <Route path="change-float" element={<ChangeFloatPage />} />
            <Route path="daily-duties" element={<DailyDutiesPage />} />
            <Route path="inventory-errors" element={<StoreAppPage app="inventoryErrors" title="Inventory Errors" />} />
            <Route path="tv-control" element={<StoreAppPage app="tvControl" title="TV Control" />} />
            <Route path="collections" element={<CollectionsPage />} />
            <Route path="door-closures" element={<DoorClosuresPage />} />
            <Route path="employee-manual" element={<EmployeeManualPage />} />
            <Route path="bug-report" element={<BugReportPage />} />
            <Route path="lottery" element={<LotteryPage />} />
            <Route path="rule-violations" element={<RuleViolationPage />} />
            <Route path="signal" element={<SignalPage />} />
            <Route path="launch" element={<LaunchSequenceHome />} />
            <Route path="launch/brief" element={<BriefingFlow />} />
            <Route path="launch/standup" element={<StandupFlow />} />
            <Route path="flight-deck" element={<FlightDeckPage />} />
            <Route path="flight-deck/settings" element={<FlightDeckSettingsPage />} />
          </Route>
          {/* Lottery SOP - standalone print page (authed, no store chrome) */}
          <Route path="/store/lottery/sop" element={<LotterySOP />} />
          {/* Attestation form - public, no auth */}
          <Route path="/attestation/:id" element={<AttestationFormPage />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </AuthGate>
  );
}
